import React from 'react';
import Tooltip from './Tooltip';
import glossary from './data/glossary.json';

// Citation patterns: §4, §4.5, §4.5.1 (also §4.5.1.2 if it ever appears).
// Word boundaries ensure we don't grab a § that's already followed by a letter.
const CITATION_RE = /§\d+(?:\.\d+){0,3}/g;

// Build a combined regex once. Longest acronym first so e.g. PBSOA matches
// before SOA. Acronyms with special regex chars (& in O&M, : in DC:AC) are
// escaped. The non-citation branch uses lookarounds so we can match terms
// that aren't bounded by \w on both sides (O&M, P50, EoW, DC:AC).
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const acronymKeys = Object.keys(glossary).sort((a, b) => b.length - a.length);
const ACRONYM_RE = new RegExp(
  // group 1: citation; group 2: acronym; group 3: optional plural s
  `(§\\d+(?:\\.\\d+){0,3})|(?<![A-Za-z0-9])(${acronymKeys.map(escapeRe).join('|')})(s?)(?![A-Za-z0-9])`,
  'g'
);

// Extract all unique citations from a text. Used to render a Sources line.
export function extractCitations(text) {
  if (!text) return [];
  const out = [];
  const seen = new Set();
  let m;
  CITATION_RE.lastIndex = 0;
  while ((m = CITATION_RE.exec(text)) !== null) {
    if (!seen.has(m[0])) {
      seen.add(m[0]);
      out.push(m[0]);
    }
  }
  return out;
}

// Render `text` as JSX, wrapping known acronyms in a Tooltip with a dotted
// underline and rendering §-citations as small orange chips.
// opts.source: 'T13' (default) or 'SPE' — controls the citation tooltip so
// SPE-sourced descriptions attribute § references to the SPE report.
// opts.citationTitle: explicit tooltip text, used to name the specific SPE
// guideline (EPC / O&M / TDD) a §-reference belongs to.
export function renderRichText(text, opts) {
  if (!text) return null;
  const source = (opts && opts.source) || 'T13';
  const citationTitle = (opts && opts.citationTitle) || (source === 'SPE'
    ? 'SolarPower Europe EPC Best Practice Guidelines v3.0 — section reference'
    : 'IEA PVPS Task 13 — T13-36:2026 report section');
  const parts = [];
  let last = 0;
  let key = 0;
  let m;
  ACRONYM_RE.lastIndex = 0;
  while ((m = ACRONYM_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) {
      // Citation
      parts.push(
        <span
          key={`c${key++}`}
          className="inline-block align-baseline text-[11px] font-mono px-1.5 py-0.5 mx-0.5 rounded"
          style={{ backgroundColor: '#FEF3E2', color: '#B45309', border: '1px solid #FCD9A8' }}
          title={citationTitle}
        >
          {m[1]}
        </span>
      );
    } else if (m[2]) {
      // Acronym (m[3] = optional plural s; preserve it in the display text)
      const def = glossary[m[2]];
      const display = m[2] + (m[3] || '');
      parts.push(
        <Tooltip key={`a${key++}`} text={def}>
          <span
            tabIndex={0}
            style={{
              borderBottom: '1px dotted #6B7280',
              cursor: 'help',
              outline: 'none',
            }}
          >
            {display}
          </span>
        </Tooltip>
      );
    }
    last = ACRONYM_RE.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
